/**
 * RTM listener for outbound:processed events from the desk socket.
 * When an agent sends an outbound message via the New Message modal, delivery is
 * confirmed asynchronously. This hook navigates to the newly created conversation.
 */
import { useCallback, useContext, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { SocketContext } from '@/contexts/rtm';
import { useDeskId } from '@/hooks/useDeskId';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { OutboundProcessedPayload, ServerToClientEvents, SocketEvent } from '@/shared/types/rtm';

/**
 * Subscribes to outbound:processed and navigates to the new conversation
 * for the agent who sent the message.
 */
export const useRtmOutboundProcessed = () => {
  const { socket } = useContext(SocketContext);
  const userId = useSelector(selectUserId);
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();
  const navigate = useNavigate();

  const handleOutboundProcessed: ServerToClientEvents[SocketEvent.outbound_processed] = useCallback(
    ({ data }: { data: OutboundProcessedPayload }) => {
      if (data.initiator_user_id !== userId) {
        return;
      }

      const conversationUrl = `/${slug}/chats/${deskId}/conversations/${data.session_id}?category=all`;
      navigate(conversationUrl);
    },
    [deskId, navigate, slug, userId]
  );

  // Bind the handler to the desk socket; cleanup on unmount or dependency change.
  useEffect(() => {
    socket?.on(SocketEvent.outbound_processed, handleOutboundProcessed);
    return () => {
      socket?.off(SocketEvent.outbound_processed, handleOutboundProcessed);
    };
  }, [handleOutboundProcessed, socket]);
};
